import { Link, type NavigateFn } from '@tanstack/react-router';
import { Edit3 } from 'lucide-react';
import {
  buildDashboardDataTableColumns,
  DashboardDataTableColumnKind,
  DashboardTableRowActionItem,
  DashboardTableRowActions,
  DashboardTableStatusTone,
  dashboardTableTextActionClassName,
} from '@shigo-ui/dashboard-ui';
import { dashboardT } from '../../../../../lib/messages';
import { formatOwnerRevenue, type OwnerListItem, SellersJsonStatus } from '../../model';

export interface OwnersTableColumnsOptions {
  canEdit: boolean;
  navigate: NavigateFn;
}

export function createOwnersTableColumns(options: OwnersTableColumnsOptions) {
  return buildDashboardDataTableColumns<OwnerListItem>([
    {
      id: 'name',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('owners.columns.owner'),
      headerClassName: 'w-64 pl-4',
      className: 'w-64 pl-4 font-medium',
      sortable: true,
      sortKey: 'name',
      value: (owner) =>
        options.canEdit && owner.id ? (
          <Link
            className={dashboardTableTextActionClassName()}
            to="/demand/owners/edit/$ownerId"
            params={{ ownerId: owner.id }}
          >
            {owner.name}
          </Link>
        ) : (
          owner.name
        ),
    },
    {
      id: 'businessDomain',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('owners.columns.businessDomain'),
      headerClassName: 'w-64',
      className: 'w-64 truncate',
      sortable: true,
      sortKey: 'businessDomain',
      value: (owner) => owner.businessDomain ?? '-',
    },
    {
      id: 'demandPartnerCount',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('owners.columns.partners'),
      headerClassName: 'w-40',
      className: 'w-40 tabular-nums',
      value: (owner) => String(owner.demandPartnerCount ?? 0),
    },
    {
      id: 'sellersJsonStatus',
      kind: DashboardDataTableColumnKind.Status,
      header: dashboardT('owners.columns.sellersJson'),
      headerClassName: 'w-40',
      className: 'w-40',
      label: (owner) => sellersJsonStatusText(owner.sellersJsonStatus),
      tone: (owner) => sellersJsonTone(owner.sellersJsonStatus),
    },
    {
      id: 'rev7d',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('owners.columns.revenue7d'),
      headerClassName: 'w-36',
      className: 'w-36 tabular-nums',
      value: (owner) => formatOwnerRevenue(owner.rev7d),
    },
    {
      id: 'actions',
      kind: DashboardDataTableColumnKind.Actions,
      hidden: !options.canEdit,
      cell: (owner) =>
        owner.id ? (
          <DashboardTableRowActions label={dashboardT('owners.actions')}>
            <DashboardTableRowActionItem
              onSelect={() =>
                void options.navigate({
                  to: '/demand/owners/edit/$ownerId',
                  params: { ownerId: owner.id },
                })
              }
            >
              <Edit3 className="h-4 w-4" aria-hidden="true" />
              {dashboardT('owners.edit')}
            </DashboardTableRowActionItem>
          </DashboardTableRowActions>
        ) : null,
    },
  ]);
}

function sellersJsonTone(status: SellersJsonStatus) {
  if (status === SellersJsonStatus.Valid) return DashboardTableStatusTone.Success;
  if (status === SellersJsonStatus.RefetchRequired) return DashboardTableStatusTone.Warning;
  return DashboardTableStatusTone.Neutral;
}

function sellersJsonStatusText(status: SellersJsonStatus) {
  if (status === SellersJsonStatus.Valid) return dashboardT('owners.sellersJson.valid');
  if (status === SellersJsonStatus.RefetchRequired) return dashboardT('owners.sellersJson.refetch');
  if (status === SellersJsonStatus.Invalid) return dashboardT('owners.sellersJson.invalid');
  return dashboardT('owners.sellersJson.unknown');
}
